import React, { useState, useEffect, useContext } from 'react';

import axiosInstance from '../axiosConfig';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AuthContext } from '../AuthContext'; // Подключение контекста для доступа к проектам
import Select from 'react-select'; // Импортируем компонент для множественного выбора

function ReportConstructorPage({ templateId, onSaveSuccess }) {
    const { projects } = useContext(AuthContext); // Получаем список проектов из контекста
    const [availableFields, setAvailableFields] = useState({});
    const [selectedFields, setSelectedFields] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]); // Состояние для выбранных проектов
    const [templateName, setTemplateName] = useState('');
    const [actionType, setActionType] = useState('ALL');
    const [rowStyles, setRowStyles] = useState({
        is_bold: false,
        is_italic: false,
        is_underline: false,
        border_style: 'NONE'
    });
    // Преобразуем fieldTypes в константу, поскольку setFieldTypes не используется
    const fieldTypes = [
        { value: 'TEXT', label: 'Текст' },
        { value: 'NUMBER', label: 'Число' },
        { value: 'CURRENCY', label: 'Денежный' },
        { value: 'DATE', label: 'Дата' },
        { value: 'DATETIME', label: 'Дата и время' }
    ];
    const actionTypeOptions = [
        { value: 'ALL', label: 'Все' },
        { value: 'IN', label: 'Поступления' },
        { value: 'OUT', label: 'Расход' },
        { value: 'RETURN', label: 'Возврат' }
    ];
    const borderStyleOptions = [
        { value: 'NONE', label: 'Нет границы' },
        { value: 'THIN', label: 'Тонкая граница' },
        { value: 'THICK', label: 'Толстая граница' },
        { value: 'DOUBLE', label: 'Двойная граница' }
    ];
    // Удалите следующую строку, если navigate не используется
    // const navigate = useNavigate();

    useEffect(() => {
        const fetchModelFields = async () => {
            try {
                const response = await axiosInstance.get('/api/get_model_fields/');
                const filteredFields = filterRelevantFields(response.data.models);
                setAvailableFields(filteredFields);
            } catch (error) {
                console.error('Ошибка при загрузке полей моделей:', error);
            }
        };

        const fetchTemplate = async (id) => {
            try {
                const response = await axiosInstance.get(`/api/get_report_template/${id}/`);
                setTemplateName(response.data.name);
                setSelectedFields(response.data.columns.map(col => ({
                    model: col.model,
                    field: col.field,
                    alias: col.alias,
                    type: col.data_format,
                    is_bold: col.is_bold,
                    is_italic: col.is_italic,
                    is_underline: col.is_underline,
                    border_style: col.border_style
                })));
                setActionType(response.data.actionType || 'ALL');
                setRowStyles(response.data.row_styles || {
                    is_bold: false,
                    is_italic: false,
                    is_underline: false,
                    border_style: 'NONE'
                });
                setSelectedProjects(response.data.projects || []); // Устанавливаем выбранные проекты

                setAvailableFields(prevFields => {
                    const newFields = { ...prevFields };
                    response.data.columns.forEach(col => {
                        if (newFields[col.model]) {
                            newFields[col.model] = newFields[col.model].filter(field => field.name !== col.field);
                        }
                    });
                    return newFields;
                });
            } catch (error) {
                console.error('Ошибка при загрузке шаблона:', error);
            }
        };

        fetchModelFields();

        if (templateId) {
            fetchTemplate(templateId);
        }
    }, [templateId]);

    const filterRelevantFields = (fields) => {
        const relevantFields = {};
        Object.keys(fields).forEach(model => {
            if (['Component', 'InventoryLog'].includes(model)) {
                relevantFields[model] = fields[model];
            }
        });
        return relevantFields;
    };

    const handleProjectChange = (selectedOptions) => {
        setSelectedProjects(selectedOptions.map(option => option.value));
    };

    const handleDragEnd = (result) => {
        const { source, destination } = result;

        if (!destination) return;

        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        if (source.droppableId === 'selectedFields' && destination.droppableId === 'selectedFields') {
            const items = Array.from(selectedFields);
            const [reorderedItem] = items.splice(source.index, 1);
            items.splice(destination.index, 0, reorderedItem);
            setSelectedFields(items);
            return;
        }

        if (source.droppableId === 'selectedFields' && destination.droppableId !== 'selectedFields') {
            const items = Array.from(selectedFields);
            const [movedItem] = items.splice(source.index, 1);
            setSelectedFields(items);

            setAvailableFields(prevFields => {
                const newFields = { ...prevFields };
                if (!newFields[movedItem.model]) {
                    newFields[movedItem.model] = [];
                }
                newFields[movedItem.model].push({ name: movedItem.field, verbose_name: movedItem.alias });
                return newFields;
            });
            return;
        }

        if (source.droppableId !== 'selectedFields' && destination.droppableId === 'selectedFields') {
            const model = source.droppableId;
            const movedField = availableFields[model][source.index];

            setAvailableFields(prevFields => {
                const newFields = { ...prevFields };
                newFields[model] = newFields[model].filter((_, idx) => idx !== source.index);
                return newFields;
            });

            setSelectedFields([...selectedFields, { model, field: movedField.name, alias: movedField.verbose_name, type: 'TEXT', is_bold: false, is_italic: false, is_underline: false, border_style: 'NONE' }]);
        }
    };

    const handleAliasChange = (index, newAlias) => {
        const updatedFields = [...selectedFields];
        updatedFields[index].alias = newAlias;
        setSelectedFields(updatedFields);
    };

    const handleFieldTypeChange = (index, newType) => {
        const updatedFields = [...selectedFields];
        updatedFields[index].type = newType;
        setSelectedFields(updatedFields);
    };

    const toggleFieldStyle = (index, style) => {
        const updatedFields = [...selectedFields];
        updatedFields[index][style] = !updatedFields[index][style];
        setSelectedFields(updatedFields);
    };

    const setFieldBorderStyle = (index, style) => {
        const updatedFields = [...selectedFields];
        updatedFields[index].border_style = style;
        setSelectedFields(updatedFields);
    };

    const toggleRowStyle = (style) => {
        setRowStyles(prevStyles => ({
            ...prevStyles,
            [style]: !prevStyles[style]
        }));
    };

    const setRowBorderStyle = (style) => {
        setRowStyles(prevStyles => ({
            ...prevStyles,
            border_style: style
        }));
    };

    const handleSave = () => {
        if (!templateName) {
            window.Toastify({
                text: "Введите название шаблона",
                duration: 3000, // Длительность отображения уведомления (в миллисекундах)
                close: true, // Кнопка закрытия
                gravity: "top", // Расположение уведомления на экране
                position: "center", // Позиция уведомления
                backgroundColor: "linear-gradient(to right, #FF5F6D, #FFC371)" // Цвет фона уведомления
            }).showToast();
            return;
        }
    
        const templateData = {
            name: templateName,
            columns: selectedFields.map(({ model, field, alias, type, is_bold, is_italic, is_underline, border_style }) => ({
                model,
                field,
                alias,
                data_format: type,
                is_bold,
                is_italic,
                is_underline,
                border_style
            })),
            row_styles: rowStyles,
            actionType: actionType,
            projects: selectedProjects // Сохраняем выбранные проекты
        };
    
        axiosInstance.post('/api/save_report_template/', templateData)
            .then(() => {
                window.Toastify({
                    text: "Шаблон успешно сохранен!",
                    duration: 3000,
                    close: true,
                    gravity: "top",
                    position: "center",
                    backgroundColor: "linear-gradient(to right, #28a745, #34c38f)"
                }).showToast();
                onSaveSuccess();
            })
            .catch(error => {
                console.error('Ошибка при сохранении шаблона:', error);
                window.Toastify({
                    text: "Ошибка при сохранении шаблона",
                    duration: 3000,
                    close: true,
                    gravity: "top",
                    position: "center",
                    backgroundColor: "linear-gradient(to right, #FF5F6D, #FFC371)"
                }).showToast();
            });
    };
    
    return (
        <div className="report-constructor-page container-fluid h-100">
            <div className="d-flex justify-content-between align-items-center">
                <h3>Конструктор шаблонов отчетов</h3>
                <div className="button-group">
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip-save">Сохранить шаблон</Tooltip>}
                    >
                        <button className="btn btn-primary" onClick={handleSave}>
                            <i className="fas fa-save"></i>
                        </button>
                    </OverlayTrigger>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip-back">Назад к отчетам</Tooltip>}
                    >
                        <button className="btn btn-secondary ml-2" onClick={onSaveSuccess}>
                            <i className="fas fa-arrow-left"></i>
                        </button>
                    </OverlayTrigger>
                </div>
            </div>
            <div className="template-controls mt-3">
                <div className="template-name-section">
                    <label>Название шаблона:</label>
                    <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Введите название шаблона"
                        className="form-control"
                    />
                </div>
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="models-section d-flex justify-content-between">
                    <div className="left-section d-flex">
                        {Object.entries(availableFields).map(([model, fields]) => (
                            <Droppable droppableId={model} key={model} direction="vertical">
                                {(provided) => (
                                    <div className="model-block" {...provided.droppableProps} ref={provided.innerRef}>
                                        <h5>{model}</h5>
                                        <ul className="field-list">
                                            {fields.map((field, index) => (
                                                <Draggable key={`${model}-${field.name}`} draggableId={`${model}-${field.name}`} index={index}>
                                                    {(provided) => (
                                                        <li ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="field-item">
                                                            {field.verbose_name}
                                                        </li>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </ul>
                                    </div>
                                )}
                            </Droppable>
                        ))}
                    </div>
                    <div className="right-section">
                        <h5>Фильтры:</h5>
                        <div className="filter-container d-flex justify-content-between">
                            <div className="form-group mr-3">
                                <label>Выберите тип действия для выгрузки:</label>
                                <Select
                                    classNamePrefix="warehouse-select"
                                    value={actionTypeOptions.find(option => option.value === actionType) || null}
                                    onChange={(option) => setActionType(option ? option.value : 'ALL')}
                                    options={actionTypeOptions}
                                    isSearchable
                                    placeholder="Выберите тип действия"
                                />
                            </div>
                            <div className="form-group">
                                <label>Выберите проекты:</label>
                                <Select
                                    isMulti
                                    options={projects.map(project => ({ value: project.id, label: project.name }))}
                                    value={projects.filter(project => selectedProjects.includes(project.id)).map(project => ({ value: project.id, label: project.name }))}
                                    onChange={handleProjectChange}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            backgroundColor: '#f8f9fa', // Задний фон выпадающего меню
                                            zIndex: 100, // Для перекрытия других объектов
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 100, // Для перекрытия других объектов
                                            backgroundColor: '#f8f9fa' // Задний фон выпадающего меню
                                        }),
                                        menuList: (provided) => ({
                                            ...provided,
                                            backgroundColor: '#f8f9fa' // Задний фон списка
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isSelected ? '#007bff' : '#fff', // Фон для выбранного и обычного элемента
                                            color: state.isSelected ? '#fff' : '#000', // Цвет текста для выбранного и обычного элемента
                                            ':hover': {
                                                backgroundColor: '#e9ecef',
                                                color: '#000'
                                            }
                                        }),
                                        multiValue: (provided) => ({
                                            ...provided,
                                            backgroundColor: '#e9ecef' // Цвет выбранных элементов
                                        }),
                                        multiValueLabel: (provided) => ({
                                            ...provided,
                                            color: '#000' // Цвет текста выбранных элементов
                                        }),
                                    }}
                                />
                            </div>
                        </div>
                        <h5 className="mt-4">Настройки для строк текста:</h5>
                        <form className="row-styles-form">
                            <div className="style-option">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="boldCheck"
                                        checked={rowStyles.is_bold}
                                        onChange={() => toggleRowStyle('is_bold')}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="boldCheck"
                                    >
                                        Полужирный
                                    </label>
                                </div>
                                <Select
                                    classNamePrefix="warehouse-select"
                                    value={borderStyleOptions.find(option => option.value === rowStyles.border_style) || null}
                                    onChange={(option) => setRowBorderStyle(option ? option.value : 'NONE')}
                                    options={borderStyleOptions}
                                    isSearchable
                                    placeholder="Выберите стиль границы"
                                />
                            </div>
                            <div className="style-option">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="italicCheck"
                                        checked={rowStyles.is_italic}
                                        onChange={() => toggleRowStyle('is_italic')}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="italicCheck"
                                    >
                                        Курсив
                                    </label>
                                </div>
                            </div>
                            <div className="style-option">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="underlineCheck"
                                        checked={rowStyles.is_underline}
                                        onChange={() => toggleRowStyle('is_underline')}
                                    />
                                    <label
                                        className="form-check-label"
                                        htmlFor="underlineCheck"
                                    >
                                        Подчеркнутый
                                    </label>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                <div className="selected-fields-section">
                    <h5>Выбранные поля</h5>
                    <Droppable droppableId="selectedFields" direction="horizontal">
                        {(provided) => (
                            <div className="selected-fields" {...provided.droppableProps} ref={provided.innerRef}>
                                <div className="selected-field-grid">
                                    {selectedFields.map(({ model, field, alias, type, is_bold, is_italic, is_underline, border_style }, index) => (
                                        <Draggable key={`${model}-${field}`} draggableId={`${model}-${field}`} index={index}>
                                            {(provided) => (
                                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="selected-field-cell">
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip id={`tooltip-${index}`}>{alias}</Tooltip>}
                                                    >
                                                        <input 
                                                            type="text"
                                                            value={alias}
                                                            onChange={(e) => handleAliasChange(index, e.target.value)}
                                                            className="form-control field-alias-input text-truncate"
                                                            onDoubleClick={(e) => e.target.focus()}
                                                        />
                                                    </OverlayTrigger>
                                                    <Select
                                                        classNamePrefix="warehouse-select"
                                                        className="field-type-select"
                                                        value={fieldTypes.find(typeOption => typeOption.value === (type || 'TEXT')) || null}
                                                        onChange={(option) => handleFieldTypeChange(index, option ? option.value : 'TEXT')}
                                                        options={fieldTypes}
                                                        isSearchable
                                                        placeholder="Тип поля"
                                                    />
                                                    <div className="style-options">
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={is_bold}
                                                                onChange={() => toggleFieldStyle(index, 'is_bold')}
                                                            />
                                                            <span style={{ fontWeight: is_bold ? 'bold' : 'normal' }}>Ж</span>
                                                        </label>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={is_italic}
                                                                onChange={() => toggleFieldStyle(index, 'is_italic')}
                                                            />
                                                            <span style={{ fontStyle: is_italic ? 'italic' : 'normal' }}>К</span>
                                                        </label>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={is_underline}
                                                                onChange={() => toggleFieldStyle(index, 'is_underline')}
                                                            />
                                                            <span style={{ textDecoration: is_underline ? 'underline' : 'none' }}>П</span>
                                                        </label>
                                                        <Select
                                                            classNamePrefix="warehouse-select"
                                                            className="field-border-style-select"
                                                            value={borderStyleOptions.find(option => option.value === border_style) || null}
                                                            onChange={(option) => setFieldBorderStyle(index, option ? option.value : 'NONE')}
                                                            options={borderStyleOptions}
                                                            isSearchable
                                                            placeholder="Граница"
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            </div>
                        )}
                    </Droppable>
                </div>
            </DragDropContext>
        </div>
    );
}

export default ReportConstructorPage;
